/* Personalised prints — live preview + line-item-property capture.
   No Shopify Plus: everything rides standard line-item properties.

   - Text fields with [data-pp-bind] live-update matching SVG nodes [data-bind].
   - The rendered SVG is serialised into [data-pp-svg-field] (a hidden
     properties[_Design preview] input) so production gets a visual reference.
   - Required fields gate add-to-cart. We listen for `submit` on document in the
     CAPTURE phase so this runs *before* Dawn's <product-form> AJAX handler
     (which is registered on the form itself, i.e. the target phase).
   - Upload-your-own products force a native multipart submit (Shopify file
     line-item properties don't work over the AJAX /cart/add endpoint).
*/
if (!customElements.get('personalised-preview')) {
  customElements.define(
    'personalised-preview',
    class PersonalisedPreview extends HTMLElement {
      connectedCallback() {
        /* SVGs to keep in lock-step. There can be more than one on the page:
           - the big preview slide injected into the product gallery, and
           - a smaller mirror preview rendered inside this component.
           Both carry [data-pp-svg]; we update them all on every input change. */
        this.svgs = Array.from(document.querySelectorAll('[data-pp-svg]'));
        this.svg = this.svgs[0] || null; // kept for backwards-compat with helpers
        this.svgField = this.querySelector('[data-pp-svg-field]');
        this.errorEl = this.querySelector('[data-pp-error]');
        this.form =
          this.closest('form') ||
          (this.dataset.formId ? document.getElementById(this.dataset.formId) : null);
        this.submitButton = this.form ? this.form.querySelector('[type="submit"][name="add"]') : null;
        this.boundFields = Array.from(this.querySelectorAll('[data-pp-bind]'));
        this.boundListFields = Array.from(this.querySelectorAll('[data-pp-bind-list]'));
        this.fileFields = Array.from(this.querySelectorAll('[data-pp-file]'));
        this.iconPicker = this.querySelector('[data-pp-icon-picker]');
        this.iconButtons = this.iconPicker ? Array.from(this.iconPicker.querySelectorAll('[data-pp-icon]')) : [];
        this.iconInputs = Array.from(this.querySelectorAll('[data-pp-icon-input]'));
        // Icon slots live inside the SVGs (one per slot per SVG). For 4 slots
        // across two SVGs we get 8 elements; the rendering loop groups them by
        // data-icon-slot.
        this.iconSlots = [];
        this.svgs.forEach((svg) => {
          this.iconSlots.push.apply(this.iconSlots, svg.querySelectorAll('[data-icon-slot]'));
        });
        this.iconCounter = this.querySelector('[data-pp-icon-counter]');
        this.iconMax = this.iconPicker ? parseInt(this.iconPicker.dataset.ppMaxIcons || '4', 10) : 0;
        this.selectedIcons = []; // ordered list of icon keys, length 0..iconMax

        this._onInput = this._onInput.bind(this);
        this._onSubmitCapture = this._onSubmitCapture.bind(this);
        this._onIconClick = this._onIconClick.bind(this);

        this.boundFields.forEach((field) => {
          // Listen for both: iOS Safari fires `change` (not `input`) on some controls.
          field.addEventListener('input', this._onInput);
          field.addEventListener('change', this._onInput);
        });
        this.boundListFields.forEach((field) => {
          field.addEventListener('input', this._onListInput.bind(this));
          field.addEventListener('change', this._onListInput.bind(this));
        });

        // Opoly: the colour-coded per-square inputs aren't form fields themselves;
        // they sync (in board order) into the hidden [data-pp-square-list] textarea,
        // which is what rides to the order + drives required-validation.
        this.squareInputs = Array.from(this.querySelectorAll('[data-pp-square]')).sort(
          (a, b) => parseInt(a.dataset.ppSquare, 10) - parseInt(b.dataset.ppSquare, 10)
        );
        this.squareList = this.querySelector('[data-pp-square-list]');
        if (this.squareInputs.length && this.squareList) {
          this._syncSquares = () => {
            this.squareList.value = this.squareInputs.map((i) => i.value.trim()).join('\n');
            // Re-run the existing list-bind + validation path.
            this.squareList.dispatchEvent(new Event('input', { bubbles: true }));
          };
          this.squareInputs.forEach((i) => {
            i.addEventListener('input', this._syncSquares);
            i.addEventListener('change', this._syncSquares);
          });
          this._syncSquares();
        }
        this.fileFields.forEach((field) => {
          field.addEventListener('change', this._onFileChange.bind(this));
        });
        this.iconButtons.forEach((btn) => btn.addEventListener('click', this._onIconClick));
        this._bindDropzones();

        // Upload products need a full-page multipart POST, not the AJAX cart.
        if (this.fileFields.length && this.form) {
          this.form.setAttribute('enctype', 'multipart/form-data');
          const pf = this.form.closest('product-form');
          if (pf) pf.dataset.nativeSubmit = 'true';
        }

        // Gate before <product-form>'s own submit handler runs.
        document.addEventListener('submit', this._onSubmitCapture, true);

        // VariantSelects may re-enable the button on variant change — re-validate
        // and toggle any variant-gated UI (e.g. the caption field on upload products).
        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
          this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
            this._toggleVariantGated(data && data.variant);
            this._validate();
          });
        }
        // The sticky-atc bar has its OWN <select name="options[...]"> that does
        // not fire Dawn's pub/sub variantChange event, so we also listen on the
        // raw inputs document-wide. Same for any variant-radios outside the
        // primary variant-selects element.
        this._onRawVariantChange = () => {
          this._toggleVariantGated(null); // re-read from SSR'd selected variant
          this._validate();
        };
        document.addEventListener('change', (event) => {
          const t = event.target;
          if (!t || !t.name || t.name.indexOf('options[') !== 0) return;
          // Give the page a tick to update the hidden id input that we read.
          setTimeout(this._onRawVariantChange, 0);
        });
        // Initial run for the variant-gated UI (variantChange hasn't fired yet on load).
        this._toggleVariantGated(null);

        // Initial paint, then again once webfonts are ready (affects text measuring).
        this._paintAll();
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this._paintAll());
        }
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this._onSubmitCapture, true);
        if (this._unsubscribe) this._unsubscribe();
      }

      _paintAll() {
        this.boundFields.forEach((field) => this._applyField(field));
        this.boundListFields.forEach((field) => this._applyListField(field));
        this._refreshSvgField();
        this._validate();
      }

      _onInput(event) {
        this._applyField(event.target);
        this._refreshSvgField();
        this._validate();
      }

      _applyField(field) {
        const key = field.dataset.ppBind;
        if (!key) return;

        let value = field.value;
        if (field.dataset.ppUppercase === 'true') {
          const upper = value.toUpperCase();
          if (upper !== value) {
            const pos = field.selectionStart;
            field.value = upper;
            try {
              field.setSelectionRange(pos, pos);
            } catch (e) {
              /* number/email inputs throw on setSelectionRange — ignore */
            }
            value = upper;
          }
        }

        const display = value.trim() === '' ? this._placeholderFor(key) : value;

        this.svgs.forEach((svg) => {
          svg.querySelectorAll('[data-bind="' + key + '"]').forEach((node) => {
            node.textContent = display;
            this._autofit(node);
          });
        });
        this.querySelectorAll('[data-pp-echo="' + key + '"]').forEach((node) => {
          node.textContent = display;
        });
      }

      _placeholderFor(key) {
        if (key === 'surname') return 'SURNAME';
        return '';
      }

      _onListInput(event) {
        this._applyListField(event.target);
        this._refreshSvgField();
        this._validate();
      }

      /* Bind a textarea's newline-separated lines to indexed SVG text elements:
         <textarea data-pp-bind-list="square"> -> [data-bind="square-1"], [data-bind="square-2"], ...
         Lines beyond what exists in the SVG are ignored; missing lines clear the slot. */
      _applyListField(field) {
        const key = field.dataset.ppBindList;
        if (!key) return;
        const lines = field.value.split(/\r?\n/);
        this.svgs.forEach((svg) => {
          const nodes = svg.querySelectorAll('[data-bind^="' + key + '-"]');
          nodes.forEach((node) => {
            const m = node.getAttribute('data-bind').match(/-(\d+)$/);
            if (!m) return;
            const idx = parseInt(m[1], 10) - 1; // data-bind="square-1" -> lines[0]
            const value = (lines[idx] || '').trim();
            node.textContent = value;
            this._autofit(node);
          });
        });
      }

      _autofit(node) {
        const max = parseFloat(node.dataset.ppAutofit);
        if (!max || typeof node.getComputedTextLength !== 'function') return;
        node.removeAttribute('textLength');
        node.removeAttribute('lengthAdjust');
        let len = 0;
        try {
          len = node.getComputedTextLength();
        } catch (e) {
          return;
        }
        if (len > max) {
          node.setAttribute('textLength', max);
          node.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        }
      }

      _refreshSvgField() {
        if (!this.svgField || !this.svg) return;
        try {
          // Serialise the first SVG (they're all kept in sync, so any will do).
          this.svgField.value = new XMLSerializer().serializeToString(this.svg);
        } catch (e) {
          /* ignore serialisation errors */
        }
      }

      _onFileChange(event) {
        const field = event.target;
        const file = field.files && field.files[0];
        const dropzone = field.closest('[data-pp-dropzone]');
        const thumb = field.dataset.ppFile ? this.querySelector(field.dataset.ppFile) : null;
        const nameEl = dropzone ? dropzone.querySelector('[data-pp-file-name]') : null;
        const sizeEl = dropzone ? dropzone.querySelector('[data-pp-file-size]') : null;

        if (file) {
          if (dropzone) dropzone.classList.add('is-pp-chosen');
          if (nameEl) nameEl.textContent = file.name;
          if (sizeEl) sizeEl.textContent = this._formatBytes(file.size);

          if (thumb && file.type.indexOf('image/') === 0) {
            const reader = new FileReader();
            reader.onload = (e) => {
              thumb.src = e.target.result;
              thumb.classList.add('is-visible');
            };
            reader.readAsDataURL(file);
          } else if (thumb) {
            // Non-image file (HEIC sometimes) — hide the thumb, name/size still show.
            thumb.removeAttribute('src');
            thumb.classList.remove('is-visible');
          }
        } else {
          // Cleared
          if (dropzone) dropzone.classList.remove('is-pp-chosen');
          if (thumb) {
            thumb.removeAttribute('src');
            thumb.classList.remove('is-visible');
          }
          if (nameEl) nameEl.textContent = '—';
          if (sizeEl) sizeEl.textContent = '';
        }
        this._validate();
      }

      /* Toggle UI blocks that depend on the chosen variant.
         Markup: <div data-pp-caption-field data-pp-caption-trigger="Yes (+£5)" hidden>
         When the trigger string appears in any of the variant's option values,
         the block is shown; otherwise it's hidden and any inputs inside are cleared. */
      _toggleVariantGated(variant) {
        const fields = Array.from(this.querySelectorAll('[data-pp-caption-field]'));
        if (!fields.length) return;
        // Resolve current variant options from arg, or fall back to the SSR'd
        // selected variant data embedded by VariantSelects.
        let opts = [];
        if (variant && Array.isArray(variant.options)) {
          opts = variant.options;
        } else {
          const sel = document.querySelector('variant-selects, variant-radios');
          if (sel) {
            const json = sel.querySelector('script[type="application/json"]');
            if (json) {
              try {
                const variants = JSON.parse(json.textContent);
                const id = parseInt(this.form && this.form.querySelector('[name="id"]') && this.form.querySelector('[name="id"]').value, 10);
                const match = variants.find((v) => v.id === id);
                if (match && match.options) opts = match.options;
              } catch (e) { /* ignore */ }
            }
          }
        }
        const optsStr = opts.join(' | ');
        fields.forEach((field) => {
          const trigger = field.dataset.ppCaptionTrigger || '';
          const show = trigger && optsStr.indexOf(trigger) >= 0;
          field.hidden = !show;
          if (!show) {
            // Clear any input inside so no caption rides to the order.
            field.querySelectorAll('input, textarea').forEach((el) => { el.value = ''; });
          }
        });
      }

      _formatBytes(bytes) {
        if (!bytes || bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      }

      _bindDropzones() {
        const dropzones = Array.from(this.querySelectorAll('[data-pp-dropzone]'));
        dropzones.forEach((dz) => {
          const input = dz.querySelector('input[type="file"]');
          if (!input) return;
          const remove = dz.querySelector('[data-pp-file-remove]');
          if (remove) {
            remove.addEventListener('click', (e) => {
              e.preventDefault();
              input.value = '';
              input.dispatchEvent(new Event('change', { bubbles: true }));
              input.focus();
            });
          }
          // Drag & drop: only intercept on the dropzone itself; let the native
          // <label for=...> handle click-to-browse.
          ['dragenter', 'dragover'].forEach((t) => {
            dz.addEventListener(t, (e) => {
              e.preventDefault();
              dz.classList.add('is-pp-dragover');
            });
          });
          ['dragleave', 'dragend'].forEach((t) => {
            dz.addEventListener(t, () => dz.classList.remove('is-pp-dragover'));
          });
          dz.addEventListener('drop', (e) => {
            e.preventDefault();
            dz.classList.remove('is-pp-dragover');
            const files = e.dataTransfer && e.dataTransfer.files;
            if (!files || !files.length) return;
            // DataTransfer can't be assigned to input.files in Safari < 14, but
            // we can do it via a fresh DataTransfer:
            try {
              const dt = new DataTransfer();
              dt.items.add(files[0]);
              input.files = dt.files;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            } catch (err) {
              /* Fallback: ignore drop, customer can still browse manually */
            }
          });
        });
      }

      _onIconClick(event) {
        const btn = event.currentTarget;
        const key = btn.dataset.ppIcon;
        if (!key) return;
        const idx = this.selectedIcons.indexOf(key);
        if (idx >= 0) {
          this.selectedIcons.splice(idx, 1);
        } else if (this.selectedIcons.length < this.iconMax) {
          this.selectedIcons.push(key);
        } else {
          // Already at max — shake the picker briefly so user notices.
          this.iconPicker.classList.add('is-pp-shake');
          setTimeout(() => this.iconPicker.classList.remove('is-pp-shake'), 320);
          return;
        }
        this._renderIcons();
        this._validate();
      }

      _renderIcons() {
        const max = this.iconMax;
        // Update button state + numbers
        this.iconButtons.forEach((btn) => {
          const key = btn.dataset.ppIcon;
          const position = this.selectedIcons.indexOf(key); // 0..3 or -1
          const isSelected = position >= 0;
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          btn.classList.toggle('is-selected', isSelected);
          const numEl = btn.querySelector('[data-pp-icon-num]');
          if (numEl) {
            if (isSelected) {
              numEl.textContent = String(position + 1);
              numEl.hidden = false;
            } else {
              numEl.textContent = '';
              numEl.hidden = true;
            }
          }
        });
        // Update every <use> across all SVGs, grouping by data-icon-slot.
        // With N SVGs and 4 slots each, this.iconSlots has N*4 elements.
        this.iconSlots.forEach((slot) => {
          const slotIdx = parseInt(slot.dataset.iconSlot, 10) - 1; // 0..3
          if (slotIdx < 0 || slotIdx >= max) return;
          const key = this.selectedIcons[slotIdx];
          const href = key ? '#crest-icon-' + key : '';
          slot.setAttribute('href', href);
          // xlink fallback for legacy renderers
          try { slot.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', href); } catch (e) { /* ignore */ }
        });
        // Update hidden form inputs (these carry the order line-item property)
        this.iconInputs.forEach((input) => {
          const slot = parseInt(input.dataset.ppIconInput, 10);
          const key = this.selectedIcons[slot - 1];
          if (!key) {
            input.value = '';
            return;
          }
          // Use the button's friendly label so production receives e.g.
          // "Yorkshire Pudding" rather than "yorkshire_pudding".
          const btn = this.iconButtons.find((b) => b.dataset.ppIcon === key);
          input.value = btn ? (btn.dataset.ppIconLabel || key) : key;
        });
        // Update counter
        if (this.iconCounter) {
          this.iconCounter.textContent = this.selectedIcons.length + ' / ' + max + ' selected';
        }
      }

      _firstInvalidField() {
        return this.boundFields
          .concat(this.boundListFields)
          .concat(this.fileFields)
          .find((field) => {
            if (field.dataset.ppRequired !== 'true') return false;
            if (field.type === 'file') return !(field.files && field.files.length);
            return field.value.trim() === '';
          });
      }

      _isValid() {
        if (this._firstInvalidField()) return false;
        if (this.iconPicker && this.selectedIcons.length < this.iconMax) return false;
        return true;
      }

      _validate() {
        const valid = this._isValid();
        if (this.submitButton) {
          this.submitButton.classList.toggle('is-personalise-incomplete', !valid);
        }
        return valid;
      }

      _onSubmitCapture(event) {
        if (event.target !== this.form) return;
        // Make sure the rendered reference is current before FormData is built.
        this._refreshSvgField();
        if (!this._isValid()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this._showError();
        }
      }

      _showError() {
        if (this.errorEl) this.errorEl.hidden = false;
        const field = this._firstInvalidField();
        if (!field) return;
        field.classList.add('is-invalid');
        field.focus();
        const clear = () => {
          field.classList.remove('is-invalid');
          if (this.errorEl) this.errorEl.hidden = true;
          field.removeEventListener('input', clear);
          field.removeEventListener('change', clear);
        };
        field.addEventListener('input', clear);
        field.addEventListener('change', clear);
      }
    }
  );
}
